/**
 * Mobile-friendly PDF download utility
 * Works on both iOS and Android
 * 
 * iOS Safari: Opens PDF in new tab, user can save via Share button
 * Android Chrome: Downloads directly to device
 * Desktop: Downloads to default download folder
 */
export function downloadPdf(blob, filename) {
  try {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    
    // Create blob URL
    const url = window.URL.createObjectURL(blob);
    
    if (isIOS) {
      // iOS Safari doesn't support download attribute well
      // Best approach: Open in new tab, user can save via Share button
      const newWindow = window.open(url, '_blank');
      
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        // Popup blocked or failed - try alternative method
        // Create a visible link that user can tap
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.style.display = 'block';
        link.style.position = 'fixed';
        link.style.top = '50%';
        link.style.left = '50%';
        link.style.transform = 'translate(-50%, -50%)';
        link.style.padding = '1rem 2rem';
        link.style.background = 'var(--accent-purple)';
        link.style.color = 'white';
        link.style.borderRadius = '8px';
        link.style.textDecoration = 'none';
        link.style.fontWeight = '600';
        link.style.zIndex = '10000';
        link.textContent = 'Tap to Open PDF';
        link.onclick = (e) => {
          e.preventDefault();
          window.open(url, '_blank');
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }, 1000);
        };
        
        document.body.appendChild(link);
        
        // Auto-remove after 3 seconds
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }
        }, 3000);
      } else {
        // Clean up URL after window opens
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      }
      
      return true;
    } else {
      // Android and Desktop: Use download attribute
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'quotation.pdf';
      
      // Make link invisible but accessible
      link.style.display = 'none';
      link.style.position = 'absolute';
      link.style.left = '-9999px';
      
      // Append to body
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Clean up after download starts
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
        window.URL.revokeObjectURL(url);
      }, 200);
      
      return true;
    }
  } catch (error) {
    console.error('Download failed:', error);
    // Final fallback: try opening in new window
    try {
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
      return true;
    } catch (fallbackError) {
      console.error('Fallback download also failed:', fallbackError);
      return false;
    }
  }
}

/**
 * Open PDF in new tab (for desktop) or download (for mobile)
 */
export function openOrDownloadPdf(blob, filename, preferDownload = false) {
  // Detect mobile devices
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  // On mobile, always download. On desktop, open in new tab (not download)
  if (isMobile || preferDownload) {
    return downloadPdf(blob, filename);
  } else {
    // Desktop: open in new tab (not download)
    const url = window.URL.createObjectURL(blob);
    const newWindow = window.open(url, '_blank');
    
    // Set the filename in the URL for better browser handling
    // Note: This opens in new tab, browser will handle display/download based on user preferences
    if (newWindow) {
      // Clean up URL after a delay (when tab is likely loaded)
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 2000);
    } else {
      // Popup blocked, fallback to download
      downloadPdf(blob, filename);
    }
    
    return newWindow;
  }
}

